import { postJSON } from 'common/services/api'
import { UseMutateFunction, useMutation, useQueryClient } from 'react-query'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { showToast } from 'common/utils'
import { useTranslation } from 'react-i18next'
import {
  CHECK_ROTA_GENERATED_QUERY_KEY,
  INSTANCES_QUERY_KEY,
  INSTANCE_QUERY_KEY,
  REVISIONS_PUBLISH_QUERY_KEY,
  REVISIONS_PUBLISH_URL,
  REVISIONS_QUERY_KEY,
  REVISION_QUERY_KEY,
  TODAY_MONITORING_QUERY_KEY,
} from './queryConstants'
import { RotaBuilderFiltersType } from 'screens/Rostering/types'

dayjs.extend(isoWeek)

type UseRevisionsPublishReturn = {
  publishRevisions: UseMutateFunction<
    any,
    unknown,
    PublishRevisionsArgs,
    unknown
  >
}

export type PublicationType =
  | 'publish_drafts'
  | 'publish_changes'
  | 'publish_all'

type PublishRevisionsArgs = {
  publicationType: PublicationType
  fromDate: string
  createdBy?: string[]
  sendPushNotification?: boolean
  sendEmail?: boolean
  filterParams?: RotaBuilderFiltersType
} & ({ intervalType: 'daily' | 'weekly' } | { toDate: string })

export type CancelRevisionPayload = {
  reason: string
  notes: string
}

const useRevisionsPublish = (): UseRevisionsPublishReturn => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const publishRevisions = useMutation(
    [REVISIONS_PUBLISH_QUERY_KEY],
    ({ filterParams = {}, ...revisions }: PublishRevisionsArgs) => {
      return postJSON({
        url: REVISIONS_PUBLISH_URL,
        isRota: true,
        json: {
          ...revisions,
          ...filterParams,
          instanceType: filterParams.typeOfEvent,
        },
      })
    },
    {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: t('CHANGES_PUBLISHED_SUCCESSFULLY'),
        })

        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        queryClient.invalidateQueries([REVISION_QUERY_KEY])
        queryClient.invalidateQueries([TODAY_MONITORING_QUERY_KEY])
        queryClient.invalidateQueries([INSTANCE_QUERY_KEY])
        queryClient.invalidateQueries([INSTANCES_QUERY_KEY])
        queryClient.invalidateQueries([CHECK_ROTA_GENERATED_QUERY_KEY])
      },
      onError: () => {
        showToast({ type: 'error', title: t('UNABLE_TO_PUBLISH_CHANGES') })
      },
    },
  )

  return {
    publishRevisions: publishRevisions.mutate,
  }
}

export default useRevisionsPublish
